import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, daysUntil, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ExternalLink, FileText, Clock, MapPin, Users, GraduationCap, IndianRupee, Share2 } from "lucide-react";
import ReviewedBy from "@/components/ReviewedBy";
import Breadcrumbs from "@/components/Breadcrumbs";
import JobDetailedSections from "@/components/JobDetailedSections";
import AdSlot from "@/components/AdSlot";
import WhatsAppPill from "@/components/WhatsAppPill";
import TelegramPill from "@/components/TelegramPill";
import StructuredInfoBlock from "@/components/StructuredInfoBlock";
import { JobContextBlock, SmartInterlinks } from "@/components/JobSemanticBlocks";
import useNoindexOnNotFound from "@/hooks/useNoindexOnNotFound";

const AD_SLOT_JOB_TOP = process.env.REACT_APP_AD_SLOT_JOB_TOP || "";
const AD_SLOT_JOB_MID = process.env.REACT_APP_AD_SLOT_JOB_MID || "";

const SITE_URL = "https://hireds.in";

function buildJobPostingJsonLd(job) {
  if (!job) return null;
  const datePosted = (job.posted_date || "").split("T")[0] || new Date().toISOString().split("T")[0];
  const validThrough = job.last_date || datePosted;
  const desc = [job.description, job.eligibility, job.how_to_apply, job.important_dates, job.application_fee, job.selection_process]
    .filter(Boolean).join("\n\n");
  const jobPosting = {
    "@type": "JobPosting",
    "title": job.title,
    "description": `<p>${(desc || job.title).replace(/\n/g, "<br/>")}</p>`,
    "identifier": { "@type": "PropertyValue", "name": job.organization, "value": job.id },
    "datePosted": datePosted,
    "validThrough": validThrough,
    "employmentType": (job.job_type || "permanent").toUpperCase() === "PERMANENT" ? "FULL_TIME" : (job.job_type || "FULL_TIME").toUpperCase().replace("-", "_"),
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.organization,
      "sameAs": job.notification_link || undefined,
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.state || "India",
        "addressRegion": job.state || "India",
        "addressCountry": "IN",
      },
    },
    ...(job.salary_min || job.salary_max ? {
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "INR",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": job.salary_min || undefined,
          "maxValue": job.salary_max || undefined,
          "unitText": "MONTH",
        },
      },
    } : {}),
    "educationRequirements": job.qualification ? {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": job.qualification,
    } : undefined,
    "applicantLocationRequirements": { "@type": "Country", "name": "India" },
    "url": `${SITE_URL}/jobs/${job.slug}`,
    "directApply": Boolean(job.apply_link),
    "reviewedBy": {
      "@type": "Person",
      "name": "Dileshwar Vitthale",
      "jobTitle": "Chief Editor, hireds.in"
    },
    "dateModified": (job.posted_date || new Date().toISOString()).split("T")[0],
  };
  const breadcrumb = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "All India Jobs", "item": `${SITE_URL}/all-india-jobs` },
      { "@type": "ListItem", "position": 3, "name": job.title, "item": `${SITE_URL}/jobs/${job.slug}` },
    ],
  };
  const graph = [jobPosting, breadcrumb];
  if (Array.isArray(job.faqs) && job.faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "mainEntity": job.faqs.filter(f => f && f.q && f.a).map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

function useStructuredData(job) {
  useEffect(() => {
    if (!job) return;
    // Remove the homepage-only extended-schema FAQPage to avoid "Duplicate FAQPage" GSC warning
    // when this job also has its own FAQs (JobPosting + FAQPage @graph)
    const extended = document.getElementById("extended-schema-jsonld");
    if (extended && Array.isArray(job.faqs) && job.faqs.length > 0) extended.remove();
    const data = buildJobPostingJsonLd(job);
    let el = document.getElementById("job-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "job-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    const oldTitle = document.title;
    document.title = `${job.title} | hireds.in`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    const oldDesc = metaDesc.content;
    // Unique, keyword-rich meta description to combat "Crawled - currently not indexed"
    const lastDateStr = job.last_date ? new Date(job.last_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
    const posts = job.total_posts ? `${job.total_posts} posts` : "";
    const qual = job.qualification ? `${job.qualification}` : "";
    const parts = [job.title, job.organization, posts && `Vacancies: ${posts}`, qual && `Eligibility: ${qual}`, lastDateStr && `Apply by ${lastDateStr}`].filter(Boolean);
    const built = parts.join(" · ");
    metaDesc.content = (built || job.description || job.title).slice(0, 160);

    // Update canonical for this job page
    let canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical ? canonical.href : null;
    if (canonical) canonical.href = `${SITE_URL}/jobs/${job.slug}`;

    // Update og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    const oldOg = ogUrl ? ogUrl.content : null;
    if (ogUrl) ogUrl.content = `${SITE_URL}/jobs/${job.slug}`;

    return () => {
      document.title = oldTitle;
      if (oldDesc !== undefined) metaDesc.content = oldDesc;
      if (oldCanonical !== null && canonical) canonical.href = oldCanonical;
      if (oldOg !== null && ogUrl) ogUrl.content = oldOg;
      const cur = document.getElementById("job-jsonld");
      if (cur) cur.remove();
    };
  }, [job]);
}

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get(`/jobs/${slug}`).then(r=>setJob(r.data)).catch(()=>setJob(false)).finally(()=>setLoading(false)); }, [slug]);
  useStructuredData(job && typeof job === "object" ? job : null);
  useNoindexOnNotFound(job === false);

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-12 text-slate-500">Loading…</div>;
  if (!job) return <div className="max-w-5xl mx-auto px-4 py-12 text-slate-500" data-testid="job-not-found">This job posting is no longer available. Browse the latest <Link to="/all-india-jobs" className="text-orange-600 underline">open vacancies</Link>.</div>;

  const d = daysUntil(job.last_date);
  const ending = d != null && d >= 0 && d <= 7;

  return (
    <article className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="job-detail">
      <Breadcrumbs items={[
        { label: "Home", to: "/" },
        { label: "All India Jobs", to: "/all-india-jobs" },
        { label: job.title },
      ]} />
      <Link to="/all-india-jobs" className="text-sm text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4"/>Back to jobs</Link>

      <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge className="bg-slate-900 text-white capitalize">{job.department.replace('-',' ')}</Badge>
          {job.is_featured && <Badge className="bg-orange-600 text-white">Featured</Badge>}
          {ending && <Badge className="bg-red-600 text-white">Ending in {d} days</Badge>}
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{job.title}</h1>
        <div className="text-slate-600 mt-1">{job.organization}</div>
        <ReviewedBy date={job.posted_date} />

        {job.llm_intro && (
          <p
            data-testid="job-llm-intro"
            className="mt-5 text-base text-slate-700 leading-relaxed border-l-4 border-orange-500 pl-4 italic"
          >
            {job.llm_intro}
          </p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat icon={Users} label="Total Posts" value={job.total_posts || "—"}/>
          <Stat icon={GraduationCap} label="Qualification" value={job.qualification}/>
          <Stat icon={MapPin} label="Location" value={job.state}/>
          <Stat icon={Clock} label="Last Date" value={fmtDate(job.last_date)} highlight={ending}/>
          <Stat icon={IndianRupee} label="Salary" value={`${fmtMoney(job.salary_min)} – ${fmtMoney(job.salary_max)}`}/>
          <Stat icon={Users} label="Age Limit" value={`${job.age_min} – ${job.age_max} yrs`}/>
          <Stat icon={FileText} label="Job Type" value={job.job_type}/>
          <Stat icon={Clock} label="Notification" value={fmtDate(job.notification_date)}/>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          {job.apply_link && <a href={job.apply_link} target="_blank" rel="noopener noreferrer" data-testid="apply-job-button"><Button className="bg-orange-600 hover:bg-orange-700 text-white h-11"><ExternalLink className="w-4 h-4 mr-2"/>Apply Online</Button></a>}
          {job.notification_link && <a href={job.notification_link} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="h-11"><FileText className="w-4 h-4 mr-2"/>Download Notification</Button></a>}
        </div>

        <WhatsAppPill variant="banner" className="mt-5" testId="job-whatsapp-banner" />
        <TelegramPill variant="banner" className="mt-3" testId="job-telegram-banner" />

        {/* Share buttons */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5"/>Share this job</span>
            <ShareButton kind="whatsapp" job={job}/>
            <ShareButton kind="twitter" job={job}/>
            <ShareButton kind="facebook" job={job}/>
            <ShareButton kind="copy" job={job}/>
          </div>
          <div className="mt-4 text-xs text-slate-500">
            <span className="font-semibold">Posted:</span> <time dateTime={(job.posted_date||"").slice(0,10)}>{fmtDate(job.posted_date)}</time>
            {" · "}<span className="font-semibold">By</span> <span>Dileshwar Vitthale</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Section title="Job Description" body={job.description}/>
        <Section title="Eligibility" body={job.eligibility}/>
        <Section title="How to Apply" body={job.how_to_apply}/>
        <Section title="Important Dates" body={job.important_dates}/>
        <Section title="Application Fee" body={job.application_fee}/>
        <Section title="Selection Process" body={job.selection_process}/>
      </div>

      {/* Always-rendered structured summary: dates + fees table + step-by-step how-to-apply.
          Ensures every job page has the same rich template even when DB fields are sparse. */}
      <StructuredInfoBlock kind="job" item={job} />

      {/* Dynamic context paragraph — unique per job, boosts semantic footprint */}
      <JobContextBlock job={job} />

      {/* AI-enriched detailed sections (Overview, Exam Pattern, Syllabus, Salary, FAQs etc) */}
      <AdSlot slot={AD_SLOT_JOB_TOP} className="mt-8" />
      <JobDetailedSections job={job} />
      <AdSlot slot={AD_SLOT_JOB_MID} />

      {/* Related Jobs - improves internal linking + helps "Crawled - not indexed" pages */}
      {Array.isArray(job.related) && job.related.length > 0 && (
        <section className="mt-10" data-testid="related-jobs">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-4" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>
            Similar {job.department ? job.department.replace('-',' ').replace(/\b\w/g, c => c.toUpperCase()) : ""} Jobs
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {job.related.map((r) => (
              <Link
                key={r.slug}
                to={`/jobs/${r.slug}`}
                data-testid={`related-job-${r.slug}`}
                className="block bg-white border border-slate-200 hover:border-orange-400 hover:shadow-sm rounded-lg p-4 transition-colors"
              >
                <div className="text-sm font-bold text-slate-900 line-clamp-2">{r.title}</div>
                <div className="text-xs text-slate-600 mt-1">{r.organization}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                  {r.total_posts ? <span><span className="font-semibold">{r.total_posts}</span> Posts</span> : null}
                  {r.last_date ? <span>Last Date: <span className="font-semibold">{fmtDate(r.last_date)}</span></span> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Smart interlinks — 5-8 deep-links to sector/state hubs and cross-verticals */}
      <SmartInterlinks job={job} variant="govt" />
    </article>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500"><Icon className="w-3.5 h-3.5"/>{label}</div>
      <div className={`mt-1 text-sm font-semibold ${highlight?'text-red-600':'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function Section({ title, body }) {
  if (!body) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h3 className="font-bold text-slate-900 mb-2" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{title}</h3>
      <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{body}</div>
    </div>
  );
}

function ShareButton({ kind, job }) {
  const url = `${SITE_URL}/jobs/${job.slug}`;
  const text = `${job.title} — ${job.organization}`;
  const map = {
    whatsapp: { label: "WhatsApp", href: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + " " + url)}`, color: "bg-green-600 hover:bg-green-700" },
    twitter: { label: "Twitter", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, color: "bg-slate-900 hover:bg-slate-800" },
    facebook: { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, color: "bg-blue-600 hover:bg-blue-700" },
  };
  if (kind === "copy") {
    return (
      <button data-testid="share-copy-btn" type="button" onClick={() => { navigator.clipboard?.writeText(url); }} className="inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-md text-sm font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100 transition-colors" aria-label="Copy job link">
        Copy Link
      </button>
    );
  }
  const c = map[kind];
  return (
    <a data-testid={`share-${kind}-btn`} href={c.href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${c.label}`} className={`inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-md text-sm font-semibold text-white ${c.color} transition-colors`}>{c.label}</a>
  );
}
